import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import {
  COACHING_ACCESS_KEYS,
  COACHING_FEATURE_TIERS,
  COACHING_FEATURES,
  COACHING_LIMIT_KEYS,
  coachingTierLabels,
  COACHING_TIERS,
  resolveCoachingPlanFeatures,
  type CoachingAccessKey,
  type CoachingFeatureGroup,
  type CoachingFeatureTier,
  type CoachingLimitKey,
  type CoachingTier,
} from '@mlm/shared';
import { useHasPerm } from '../lib/auth-store';
import { getTenantFeatures, patchTenantFeatures } from '../lib/api';
import { toast } from '@mlm/ui/components/toast';

const GROUP_LABEL: Record<CoachingFeatureGroup, string> = {
  coach: 'Coach and the daily loop',
  align: 'Align',
  manifest: 'Manifest',
  achieve: 'Achieve',
};

const GROUP_ORDER: CoachingFeatureGroup[] = ['coach', 'align', 'manifest', 'achieve'];

type AccessDraft = Partial<Record<CoachingAccessKey, CoachingFeatureTier>>;
type LimitDraft = Partial<Record<CoachingLimitKey, Partial<Record<CoachingTier, number>>>>;

/**
 * Coaching -> Features: the lowest plan that carries each coaching feature.
 *
 * A feature carries a MINIMUM plan rather than a tick per plan, so a higher
 * plan always includes everything a lower one has and "Pro but not Elite" is
 * not expressible. `Nobody` exists because a tenant that wants no one to have
 * a feature cannot say that with a minimum alone.
 *
 * The editor always shows the table IN FORCE, which for an untouched tenant is
 * whatever the old Settings switches said. Saving writes only what is on
 * screen; `Reset` clears the saved table so every key falls back to those
 * switches again.
 */
export function FeaturesTab() {
  const canWrite = useHasPerm('tenant.features.write');
  const queryClient = useQueryClient();
  const features = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const tierLabels = coachingTierLabels(features.data?.coaching);

  const [access, setAccess] = useState<AccessDraft>({});
  const [limits, setLimits] = useState<LimitDraft>({});
  const [loaded, setLoaded] = useState(false);

  // Seed from what is IN FORCE, not from what was saved: an untouched tenant
  // should see the switches' answer rather than an empty form that would look
  // like every feature was free.
  useEffect(() => {
    if (!features.data || loaded) return;
    const c = features.data.coaching;
    const resolved = resolveCoachingPlanFeatures(
      {
        free_daily_messages: c.free_daily_messages,
        pro_daily_messages: c.pro_daily_messages,
        free_active_goals: c.free_active_goals,
        streak_freeze_pro: c.streak_freeze_pro,
        chart_guidance_pro_only: c.chart_guidance_pro_only,
        tools_pro_only: c.tools_pro_only,
      },
      c.plan_features ?? null,
    );
    setAccess({ ...resolved.access });
    setLimits(
      Object.fromEntries(
        COACHING_LIMIT_KEYS.map((k) => [k, { ...resolved.limits[k] }]),
      ) as LimitDraft,
    );
    setLoaded(true);
  }, [features.data, loaded]);

  const save = useMutation({
    mutationFn: () =>
      patchTenantFeatures({
        coaching: {
          ...features.data!.coaching,
          plan_features: {
            access: access as Record<string, CoachingFeatureTier>,
            limits: limits as Record<string, Record<string, number>>,
          },
        },
      }),
    onSuccess: () => {
      toast.success('Saved. Members see this on their next request.');
      void queryClient.invalidateQueries({ queryKey: ['tenant-features'] });
    },
  });

  const reset = useMutation({
    mutationFn: () =>
      patchTenantFeatures({
        coaching: { ...features.data!.coaching, plan_features: null },
      }),
    onSuccess: () => {
      toast.success('Cleared. Every feature follows the Settings switches again.');
      setLoaded(false);
      void queryClient.invalidateQueries({ queryKey: ['tenant-features'] });
    },
  });

  if (features.isPending) return <p className="p-4 text-sm text-muted-foreground">Loading…</p>;
  if (features.isError)
    return <p className="p-4 text-sm text-destructive">Could not load the tenant settings.</p>;
  if (!features.data.coaching_enabled) {
    return (
      <Card className="mt-4">
        <CardContent className="p-6 text-sm text-muted-foreground">
          The coach app is off for this tenant, so there is nothing to place. Turn it on in Settings
          → Coaching first.
        </CardContent>
      </Card>
    );
  }

  const saved = features.data.coaching.plan_features != null;

  return (
    <div className="mt-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>What each plan includes</CardTitle>
          <p className="text-sm text-muted-foreground">
            The lowest plan that carries each feature. A higher plan always includes everything a
            lower one has. Pick <strong>Nobody</strong> to switch a feature off for everyone, which
            hides it rather than showing members a lock they cannot open.
            {!saved && (
              <>
                {' '}
                Nothing is saved yet, so this is what the Settings switches currently say. Saving
                takes over from them.
              </>
            )}
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {GROUP_ORDER.map((group) => {
            const keys = COACHING_ACCESS_KEYS.filter((k) => COACHING_FEATURES[k].group === group);
            if (keys.length === 0) return null;
            return (
              <section key={group} className="space-y-2">
                <h3 className="text-sm font-semibold">{GROUP_LABEL[group]}</h3>
                <div className="divide-y rounded-md border">
                  {keys.map((key) => {
                    const def = COACHING_FEATURES[key];
                    return (
                      <div
                        key={key}
                        className="flex flex-wrap items-center justify-between gap-3 p-3"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium">{def.label}</p>
                          <p className="text-xs text-muted-foreground">{def.hint}</p>
                        </div>
                        <Select
                          value={access[key] ?? 'free'}
                          onValueChange={(v) =>
                            setAccess((a) => ({ ...a, [key]: v as CoachingFeatureTier }))
                          }
                          disabled={!canWrite}
                        >
                          <SelectTrigger className="w-44">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COACHING_FEATURE_TIERS.map((t) => (
                              <SelectItem key={t} value={t}>
                                {t === 'off' ? 'Nobody' : tierLabels[t]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}

          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Numbers</h3>
            <div className="divide-y rounded-md border">
              {COACHING_LIMIT_KEYS.map((key) => {
                const def = COACHING_FEATURES[key];
                return (
                  <div key={key} className="space-y-2 p-3">
                    <div>
                      <p className="text-sm font-medium">{def.label}</p>
                      <p className="text-xs text-muted-foreground">{def.hint}</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {COACHING_TIERS.map((tier) => (
                        <label key={tier} className="space-y-1">
                          <span className="block text-xs text-muted-foreground">
                            {tierLabels[tier]}
                          </span>
                          <Input
                            type="number"
                            min={0}
                            className="w-28"
                            disabled={!canWrite}
                            value={limits[key]?.[tier] ?? 0}
                            onChange={(e) =>
                              setLimits((l) => ({
                                ...l,
                                [key]: { ...l[key], [tier]: Math.max(0, Number(e.target.value)) },
                              }))
                            }
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {canWrite && (
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
              {saved && (
                <Button variant="outline" onClick={() => reset.mutate()} disabled={reset.isPending}>
                  Reset to the Settings switches
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
